import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { safeLocalStorage } from '@/lib/safe-storage';
// Цикла импортов нет: address-store тянет только @/lib/api/delivery и safe-storage.
import { useAddressStore } from './address-store';

// ========== TYPES ==========

export type CheckoutStep =
  | 'cart'           // Step 1: Cart items
  | 'roulette'       // Step 2: Roulette spin
  | 'bonus'          // Step 3: First order bonus check
  | 'extras'         // Step 4: Additional items (sauces, etc)
  | 'delivery-time'  // Step 5: Delivery time selection
  | 'delivery-info'  // Step 6: Delivery details
  | 'summary';       // Step 7: Order summary (payment redirects to PayKeeper)

export const CHECKOUT_STEPS: CheckoutStep[] = [
  'cart',
  'roulette',
  'bonus',
  'extras',
  'delivery-time',
  'delivery-info',
  'summary',
];

export const STEP_TITLES: Record<CheckoutStep, string> = {
  'cart': 'Корзина',
  'roulette': 'Рулетка',
  'bonus': 'Подарок за первый заказ',
  'extras': 'Добавить к заказу',
  'delivery-time': 'Время доставки',
  'delivery-info': 'Данные доставки',
  'summary': 'Проверка заказа',
};

// Delivery time type
export type DeliveryTimeType = 'now' | 'scheduled';

// Order contact data
export interface OrderContact {
  name: string;
  phone: string;
}

// Delivery details
export interface DeliveryDetails {
  address: string;
  apartment: string;
  entrance: string;
  floor: string;
  intercom: string;
  isPrivateHouse: boolean;
  /**
   * false — человек нажал «нет домофона».
   * В домах с открытым подъездом писать в это поле нечего: раньше он ставил
   * прочерк или бросал заказ. Пустой домофон при hasIntercom=false —
   * осознанный ответ, и валидация его пропускает.
   */
  hasIntercom: boolean;
  comment: string;
  /** id адреса из книги, который сейчас подставлен. Нужен, чтобы обновлять его, а не плодить копии. */
  savedAddressId: number | null;
}

// Delivery time
export interface DeliveryTime {
  type: DeliveryTimeType;
  selectedDate: string;
  selectedTime: string;
}

// Order preferences
export interface OrderPreferences {
  noGreens: boolean;
  needCall: boolean;
}

// Bonus state
export interface BonusState {
  isFirstOrder: boolean;
  isFirstOrderChecked: boolean;
  isCheckingFirstOrder: boolean;
  firstOrderCheckSuccess: boolean;
  bonusRollAdded: boolean;
}

// Extra item in checkout
export interface ExtraItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
  image_url: string;
  sku?: string;
}

// ========== STORE STATE ==========

interface CheckoutState {
  // UI State
  isOpen: boolean;
  currentStep: CheckoutStep;
  
  // Contact & Delivery
  contact: OrderContact;
  deliveryDetails: DeliveryDetails;
  deliveryTime: DeliveryTime;
  preferences: OrderPreferences;
  
  // Bonus
  bonusState: BonusState;
  
  // Extras
  extras: ExtraItem[];
  
  // Promo
  appliedPromoCode: string | null;

  // Loyalty coins
  loyaltyCoinsUsed: number;

  // Validation
  errors: Record<string, boolean>;
  showValidationMessage: boolean;
  
  // Loading states
  isSubmitting: boolean;
  
  // Payment
  paymentUrl: string | null;
  paymentOrderId: number | null;
  
  // ========== ACTIONS ==========
  
  // UI Actions
  openCheckout: () => void;
  closeCheckout: () => void;
  setStep: (step: CheckoutStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (stepIndex: number) => void;
  
  // Contact Actions
  setContact: (data: Partial<OrderContact>) => void;
  
  // Delivery Actions
  setDeliveryDetails: (data: Partial<DeliveryDetails>) => void;
  setDeliveryTime: (data: Partial<DeliveryTime>) => void;
  
  // Preferences Actions
  setPreferences: (data: Partial<OrderPreferences>) => void;
  
  // Bonus Actions
  setBonusState: (data: Partial<BonusState>) => void;
  resetBonusState: () => void;
  
  // Extras Actions
  setExtras: (extras: ExtraItem[]) => void;
  updateExtraQuantity: (id: number, quantity: number) => void;
  
  // Promo Actions
  setAppliedPromoCode: (code: string | null) => void;

  // Loyalty Actions
  setLoyaltyCoins: (coins: number) => void;

  // Validation Actions
  setErrors: (errors: Record<string, boolean>) => void;
  setError: (field: string, hasError: boolean) => void;
  clearErrors: () => void;
  setShowValidationMessage: (show: boolean) => void;
  
  // Submit Actions
  setIsSubmitting: (value: boolean) => void;
  
  // Payment Actions
  setPaymentData: (url: string, orderId: number) => void;
  clearPaymentData: () => void;
  
  // Reset
  resetCheckout: () => void;
}

// ========== INITIAL STATE ==========

const initialContact: OrderContact = {
  name: '',
  phone: '',
};

const initialDeliveryDetails: DeliveryDetails = {
  address: '',
  apartment: '',
  entrance: '',
  floor: '',
  intercom: '',
  isPrivateHouse: false,
  hasIntercom: true,
  comment: '',
  savedAddressId: null,
};

const initialDeliveryTime: DeliveryTime = {
  type: 'now',
  selectedDate: '',
  selectedTime: '',
};

const initialPreferences: OrderPreferences = {
  noGreens: false,
  needCall: false,
};

const initialBonusState: BonusState = {
  isFirstOrder: false,
  isFirstOrderChecked: false,
  isCheckingFirstOrder: false,
  firstOrderCheckSuccess: false,
  bonusRollAdded: false,
};

// ========== STORE ==========

export const useCheckoutStore = create<CheckoutState>()(
  persist(
    immer((set) => ({
      // Initial State
      isOpen: false,
      currentStep: 'cart',
      contact: initialContact,
      deliveryDetails: initialDeliveryDetails,
      deliveryTime: initialDeliveryTime,
      preferences: initialPreferences,
      bonusState: initialBonusState,
      extras: [],
      appliedPromoCode: null,
      loyaltyCoinsUsed: 0,
      errors: {},
      showValidationMessage: false,
      isSubmitting: false,
      paymentUrl: null,
      paymentOrderId: null,

      // UI Actions
      openCheckout: () => {
        // ── Гейт адреса ────────────────────────────────────────────────
        // Без адреса корзина бесполезна: минимальная сумма считается нулевой,
        // доставка не входит в «Итого», и человек узнаёт правду только на
        // шестом шаге. Поэтому сначала адрес.
        //
        // Читаем через getState(), без подписки: экшен вызывается из клика,
        // то есть заведомо после того, как persist поднял адрес из localStorage.
        // Проверка на этапе рендера дала бы расхождение SSR и первого кадра.
        //
        // Условие — отсутствие адреса, а НЕ отсутствие зоны. У человека вне
        // зоны адрес есть; запирать его перед корзиной значило бы оставить
        // в тупике без объяснения. Его останавливает шаг 6, где написано,
        // что доставки по этому адресу нет.
        if (!useAddressStore.getState().selectedAddress) {
          // Вместо тоста и мгновенной полноэкранной карты — окно с объяснением,
          // зачем адрес нужен. Карту открывает уже кнопка в этом окне.
          useAddressStore.getState().openRequiredPrompt('checkout');

          // Отвал на гейте иначе не виден: checkout_started стоит ниже и для
          // этих людей не отправится, а cart_opened в cart-store мёртв —
          // setCartOpen никто не вызывает. Без своего события мы бы просто
          // не знали, сколько человек упёрлось в запрос адреса и ушло.
          import('@/lib/source-tracker').then(({ captureEvent }) => {
            captureEvent('address_gate_blocked', { reason: 'no_address', source: 'checkout' });
          }).catch(() => { /* ignore */ });
          return;
        }

        set((state) => {
          // If there's a pending payment from a previous session, reset to cart
          if (state.paymentUrl) {
            state.paymentUrl = null;
            state.paymentOrderId = null;
            state.currentStep = 'cart';
          }
          state.isOpen = true;
        });

        // ── Behavioral tracking: checkout_started ──
        // Стоит ПОСЛЕ гейта: иначе событие фиксировало бы открытия корзины,
        // которых не было, и отвал безадресных пропал бы из воронки.
        import('@/lib/source-tracker').then(({ captureEvent }) => {
          captureEvent('checkout_started');
        }).catch(() => { /* ignore */ });
      },

      closeCheckout: () => {
        set((state) => {
          state.isOpen = false;
        });
      },

      setStep: (step) => {
        set((state) => {
          state.currentStep = step;
          state.showValidationMessage = false;
        });
      },

      nextStep: () => {
        set((state) => {
          const currentIndex = CHECKOUT_STEPS.indexOf(state.currentStep);
          if (currentIndex < CHECKOUT_STEPS.length - 1) {
            state.currentStep = CHECKOUT_STEPS[currentIndex + 1];
            state.showValidationMessage = false;
          }
        });
      },

      prevStep: () => {
        set((state) => {
          const currentIndex = CHECKOUT_STEPS.indexOf(state.currentStep);
          if (currentIndex > 0) {
            state.currentStep = CHECKOUT_STEPS[currentIndex - 1];
            state.showValidationMessage = false;
          }
        });
      },

      goToStep: (stepIndex) => {
        set((state) => {
          if (stepIndex >= 0 && stepIndex < CHECKOUT_STEPS.length) {
            state.currentStep = CHECKOUT_STEPS[stepIndex];
            state.showValidationMessage = false;
          }
        });
      },

      // Contact Actions
      setContact: (data) => {
        set((state) => {
          state.contact = { ...state.contact, ...data };
        });
      },

      // Delivery Actions
      setDeliveryDetails: (data) => {
        set((state) => {
          state.deliveryDetails = { ...state.deliveryDetails, ...data };
        });
      },

      setDeliveryTime: (data) => {
        set((state) => {
          state.deliveryTime = { ...state.deliveryTime, ...data };
        });
      },

      // Preferences Actions
      setPreferences: (data) => {
        set((state) => {
          state.preferences = { ...state.preferences, ...data };
        });
      },

      // Bonus Actions
      setBonusState: (data) => {
        set((state) => {
          state.bonusState = { ...state.bonusState, ...data };
        });
      },

      resetBonusState: () => {
        set((state) => {
          state.bonusState = initialBonusState;
        });
      },

      // Extras Actions
      setExtras: (extras) => {
        set((state) => {
          state.extras = extras;
        });
      },

      updateExtraQuantity: (id, quantity) => {
        set((state) => {
          const extra = state.extras.find((e) => e.id === id);
          if (extra) {
            extra.quantity = Math.max(0, quantity);
          }
        });
      },

      // Promo Actions
      setAppliedPromoCode: (code) => {
        set((state) => {
          state.appliedPromoCode = code;
        });
      },

      // Loyalty Actions
      setLoyaltyCoins: (coins) => {
        set((state) => {
          state.loyaltyCoinsUsed = Math.max(0, Math.floor(coins));
        });
      },

      // Validation Actions
      setErrors: (errors) => {
        set((state) => {
          state.errors = errors;
        });
      },

      setError: (field, hasError) => {
        set((state) => {
          state.errors[field] = hasError;
        });
      },

      clearErrors: () => {
        set((state) => {
          state.errors = {};
          state.showValidationMessage = false;
        });
      },

      setShowValidationMessage: (show) => {
        set((state) => {
          state.showValidationMessage = show;
        });
      },

      // Submit Actions
      setIsSubmitting: (value) => {
        set((state) => {
          state.isSubmitting = value;
        });
      },

      // Payment Actions
      setPaymentData: (url, orderId) => {
        set((state) => {
          state.paymentUrl = url;
          state.paymentOrderId = orderId;
        });
      },

      clearPaymentData: () => {
        set((state) => {
          state.paymentUrl = null;
          state.paymentOrderId = null;
        });
      },

      // Reset
      resetCheckout: () => {
        set((state) => {
          state.currentStep = 'cart';
          state.contact = initialContact;
          // Адрес и данные квартиры НЕ стираем.
          //
          // Раньше resetCheckout() обнулял их целиком, а вызывается он на
          // странице успеха и после оплаты — то есть квартиру, подъезд, этаж и
          // домофон терял ровно тот, кто только что заказал. В следующий раз он
          // вводил всё заново.
          //
          // Сбрасываем только то, что относится к КОНКРЕТНОМУ заказу:
          // комментарий курьеру.
          state.deliveryDetails = { ...state.deliveryDetails, comment: '' };
          state.deliveryTime = initialDeliveryTime;
          state.preferences = initialPreferences;
          state.bonusState = initialBonusState;
          state.extras = [];
          state.appliedPromoCode = null;
          state.loyaltyCoinsUsed = 0;
          state.errors = {};
          state.showValidationMessage = false;
          state.isSubmitting = false;
          state.paymentUrl = null;
          state.paymentOrderId = null;
        });
      },
    })),
    {
      name: 'aziatochka-checkout',
      storage: createJSONStorage(() => safeLocalStorage),
      partialize: (state) => ({
        contact: state.contact,
        deliveryDetails: state.deliveryDetails,
        deliveryTime: state.deliveryTime,
        preferences: state.preferences,
        extras: state.extras,
      }),
      // Persist по умолчанию заменяет вложенный объект целиком. У людей, чей
      // localStorage записан прошлой версией, в deliveryDetails нет hasIntercom
      // и savedAddressId — они пришли бы как undefined, и «нет домофона»
      // сломалось бы у действующих клиентов. Досыпаем недостающие поля.
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<CheckoutState>;
        return {
          ...current,
          ...saved,
          contact: { ...current.contact, ...(saved.contact ?? {}) },
          deliveryDetails: { ...current.deliveryDetails, ...(saved.deliveryDetails ?? {}) },
          deliveryTime: { ...current.deliveryTime, ...(saved.deliveryTime ?? {}) },
          preferences: { ...current.preferences, ...(saved.preferences ?? {}) },
          extras: Array.isArray(saved.extras) ? saved.extras : current.extras,
        };
      },
    }
  )
);

// ========== SELECTORS ==========

export const selectCurrentStep = (state: CheckoutState) => state.currentStep;
export const selectCurrentStepIndex = (state: CheckoutState) => 
  CHECKOUT_STEPS.indexOf(state.currentStep);
export const selectIsCheckoutOpen = (state: CheckoutState) => state.isOpen;
export const selectContact = (state: CheckoutState) => state.contact;
export const selectDeliveryDetails = (state: CheckoutState) => state.deliveryDetails;
export const selectDeliveryTime = (state: CheckoutState) => state.deliveryTime;
export const selectPreferences = (state: CheckoutState) => state.preferences;
export const selectBonusState = (state: CheckoutState) => state.bonusState;
export const selectExtras = (state: CheckoutState) => state.extras;
export const selectExtrasTotal = (state: CheckoutState) => 
  state.extras.reduce((sum, extra) => sum + extra.price * extra.quantity, 0);
export const selectIsSubmitting = (state: CheckoutState) => state.isSubmitting;
export const selectPaymentUrl = (state: CheckoutState) => state.paymentUrl;
export const selectPaymentOrderId = (state: CheckoutState) => state.paymentOrderId;

// Computed: Get step progress percentage
export const selectStepProgress = (state: CheckoutState) => {
  const currentIndex = CHECKOUT_STEPS.indexOf(state.currentStep);
  return ((currentIndex + 1) / CHECKOUT_STEPS.length) * 100;
};

// Computed: Is on first step
export const selectIsFirstStep = (state: CheckoutState) => 
  state.currentStep === 'cart';

// Computed: Is on last step
export const selectIsLastStep = (state: CheckoutState) => 
  state.currentStep === 'summary';

